import { inject, Injectable } from "@angular/core";
import { API_PATHS } from "../../common/constants/api-paths";
import { Brand } from "../../common/constants/brand";
import { ApiResponse } from "../../common/models/api-response.model";
import { mockSmartVehicleSearchResponse } from "../../common/utils/api-mocks";
import { isEnumKeyOrValue } from "../../common/utils/utils";
import { BaseService } from "../../common/services/base-service";
import { CacheService } from "../../common/services/cache-service";
import { environment } from "../../../environments/environment";
import { VehicleSearchService } from "../vehicle-search/vehicle-search-service";
import { BehaviorSubject, firstValueFrom, Observable } from "rxjs";
import {
  CarSearchResponse,
  CountryListResponse,
  EasyAccessSmartSearchResponse,
  VehicleContext,
  VehicleMakeListResponse,
  VehicleModelListResponse,
  VehicleMotorListResponse,
  VehicleMotorResult,
  VehicleRegistrationSearchItem,
  VehicleSearchMinimalResponse,
  VehicleSearchModel,
  VehicleSearchResponse,
} from "./vehicle-search-easyaccess-model";

type VehicleApiResponse = ApiResponse<
  | VehicleSearchResponse
  | VehicleSearchMinimalResponse[]
  | VehicleMakeListResponse
  | VehicleModelListResponse
  | VehicleMotorListResponse
  | CarSearchResponse
  | VehicleMotorResult
>;

@Injectable({ providedIn: "root" })
export class VehicleSearchEasyaccessService {
  private readonly baseService = inject(BaseService);
  private readonly cacheService = inject(CacheService);
  private readonly sharedVehicleSearchService = inject(VehicleSearchService);
  private readonly vehicleDetailsSubject = new BehaviorSubject<VehicleContext | null>(null);

  readonly vehicleDetailsValue = this.vehicleDetailsSubject.asObservable();

  getVehicle(vehicleSearchModel: VehicleSearchModel): Promise<VehicleApiResponse> {
    return environment.STUBS ? this.fetchVehicle(vehicleSearchModel) : Promise.resolve(null);
  }

  private async fetchVehicle(vehicleSearchModel: VehicleSearchModel): Promise<VehicleApiResponse> {
    try {
      let url = `${API_PATHS.VEHICLE.VEHICLE_SEARCH}?searchType=${encodeURIComponent(vehicleSearchModel.searchType)}`;

      if (vehicleSearchModel.vin) url += `&vin=${encodeURIComponent(vehicleSearchModel.vin)}`;
      if (vehicleSearchModel.vis) url += `&vis=${encodeURIComponent(vehicleSearchModel.vis)}`;
      if (vehicleSearchModel.countryCode) url += `&countryCode=${encodeURIComponent(vehicleSearchModel.countryCode)}`;
      if (vehicleSearchModel.lpn) url += `&lpn=${encodeURIComponent(vehicleSearchModel.lpn)}`;
      if (vehicleSearchModel.brand) url += `&brand=${encodeURIComponent(vehicleSearchModel.brand)}`;
      if (vehicleSearchModel.model) url += `&model=${encodeURIComponent(vehicleSearchModel.model)}`;
      if (vehicleSearchModel.ktype) url += `&ktype=${encodeURIComponent(vehicleSearchModel.ktype)}`;
      if (vehicleSearchModel.KBANumber) url += `&KBANumber=${encodeURIComponent(vehicleSearchModel.KBANumber)}`;
      if (vehicleSearchModel.swissType) url += `&swissType=${encodeURIComponent(vehicleSearchModel.swissType)}`;
      if (vehicleSearchModel.NatCode) url += `&NatCode=${encodeURIComponent(vehicleSearchModel.NatCode)}`;

      return await this.baseService.get<VehicleApiResponse>(url);
    } catch (error) {
      console.error("Error fetching vehicle details:", error);
      throw new Error(error instanceof Error ? error.message : JSON.stringify(error));
    }
  }

  getCountryList(): Promise<ApiResponse<CountryListResponse>> {
    return environment.STUBS ? this.fetchCountryList() : Promise.resolve(null);
  }

  private async fetchCountryList(): Promise<ApiResponse<CountryListResponse>> {
    try {
      return await this.baseService.get<ApiResponse<CountryListResponse>>(API_PATHS.VEHICLE.GET_COUNTRY_LIST);
    } catch (error) {
      console.error("Error fetching country list:", error);
      throw new Error(error instanceof Error ? error.message : JSON.stringify(error));
    }
  }

  getVehicleRegistrationSearchData(): Observable<ApiResponse<VehicleRegistrationSearchItem[]>> | null {
    return null;
  }

  async smartSearch(language: string, searchTerm: string): Promise<EasyAccessSmartSearchResponse> {
    if (!environment.STUBS) {
      return firstValueFrom(mockSmartVehicleSearchResponse());
    }

    try {
      const params = { lang: language, key: searchTerm };
      return await this.baseService.get<EasyAccessSmartSearchResponse>(
        API_PATHS.VEHICLE.SMART_SEARCH,
        params,
        null,
        false
      );
    } catch (error) {
      console.error("Error calling smartSearch API:", error);
      throw new Error(error instanceof Error ? error.message : JSON.stringify(error));
    }
  }

  updateVehicleDetails(info: VehicleContext | null): void {
    this.vehicleDetailsSubject.next(info);
    this.sharedVehicleSearchService.updateVehicleDetails(info);
  }

  getVehicleDetails(): Observable<VehicleContext | null> {
    return this.vehicleDetailsValue;
  }

  isBrandInternal(brandCode?: string, brandName?: string, brandDescription?: string): boolean {
    return isEnumKeyOrValue(brandCode, Brand)
      || isEnumKeyOrValue(brandName, Brand)
      || isEnumKeyOrValue(brandDescription, Brand);
  }

  isBrandNameInternal(brandName?: string): boolean {
    return isEnumKeyOrValue(brandName, Brand);
  }
}
